import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { adminApi } from "../../services/adminApi";
import { themeAnalyticsApi } from "../../services/themeAnalyticsApi";
import { Card } from "../../components/common/Card";
import { Spinner } from "../../components/common/Spinner";
import { StatTile } from "../../components/common/StatTile";
import { GapNotice } from "../../components/common/GapNotice";
import { DataTable } from "../../components/common/DataTable";

const THEME_COLORS = { Basic: "#3b82c4", Advanced: "#f2a900", Custom: "#2e8b57" };

export function ThemeAnalyticsPage() {
  const [distribution, setDistribution] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [timeToUpgrade, setTimeToUpgrade] = useState(null);
  const [migrationPaths, setMigrationPaths] = useState(null);

  useEffect(() => {
    adminApi.themeUsage().then((r) => setDistribution(r.theme_usage)).catch(() => setDistribution([]));
    themeAnalyticsApi.upgradeFunnel().then((r) => setFunnel(r)).catch(() => setFunnel(null));
    themeAnalyticsApi.timeToUpgrade().then((r) => setTimeToUpgrade(r)).catch(() => setTimeToUpgrade(null));
    themeAnalyticsApi.migrationPaths().then((r) => setMigrationPaths(r.paths)).catch(() => setMigrationPaths(null));
  }, []);

  if (!distribution) return <Spinner />;

  const totalUsers = distribution.reduce((sum, d) => sum + d.user_count, 0) || 1;

  return (
    <div>
      <h1>Theme Analytics</h1>

      <div className="section-spacing" style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        {distribution.map((d) => (
          <StatTile
            key={d.theme_name}
            label={d.theme_name}
            value={d.user_count}
            sublabel={`${((d.user_count / totalUsers) * 100).toFixed(1)}% of users`}
          />
        ))}
      </div>

      <Card title="Theme Distribution" className="section-spacing">
        {distribution.length === 0 ? (
          <p>No theme usage data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={distribution} dataKey="user_count" nameKey="theme_name" outerRadius={120} label>
                {distribution.map((d) => (
                  <Cell key={d.theme_name} fill={THEME_COLORS[d.theme_name] || "#888"} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Upgrade Funnel: Basic → Advanced" className="section-spacing">
        {funnel ? (
          <DataTable
            rows={funnel.transitions}
            rowKey="from_theme"
            columns={[
              { key: "from_theme", header: "From" },
              { key: "to_theme", header: "To" },
              { key: "transition_count", header: "Users" },
              { key: "drop_off_pct", header: "Drop-off %", render: (r) => `${r.drop_off_pct}%` },
            ]}
          />
        ) : (
          <GapNotice>
            Needs a new admin endpoint aggregating app.ThemeUsageAnalytics's activation_date/upgrade_date columns
            (which already exist and are populated) into Basic→Advanced transition counts and drop-off points.
          </GapNotice>
        )}
      </Card>

      <Card title="Time-to-Upgrade" className="section-spacing">
        {timeToUpgrade ? (
          <StatTile label="Average days before upgrading" value={timeToUpgrade.avg_days} />
        ) : (
          <GapNotice>
            Would be AVG(DATEDIFF(day, activation_date, upgrade_date)) over app.ThemeUsageAnalytics -- no endpoint
            computes it yet.
          </GapNotice>
        )}
      </Card>

      <Card title="Theme Migration Paths">
        {migrationPaths ? (
          <DataTable
            rows={migrationPaths}
            rowKey="path"
            columns={[
              { key: "path", header: "Path" },
              { key: "user_count", header: "Users" },
            ]}
          />
        ) : (
          <GapNotice>
            Common multi-step paths (e.g. Basic → Custom → Advanced) need the same ThemeUsageAnalytics aggregation as
            the funnel above, grouped by full sequence rather than single transitions.
          </GapNotice>
        )}
      </Card>
    </div>
  );
}
